import React, { useRef } from 'react';
import { arrayOf, func, object, shape, string } from 'prop-types';
import { noop } from 'lodash';
import { I18n } from '@fiverr-private/i18n-react';
import { Button, Icon } from '@fiverr-private/fit';
import { l_chevron_left } from '@fiverr-private/icons';
import { marketplaceIntegration } from '@fiverr-private/logo_maker_analytics/dist/esm/mixpanel';
import {
    LogoProjectListings,
    LOGO_PROJECT_CARD,
    LISTING_SOURCE,
} from '@fiverr-private/variations_display/src';
import StaticFilters from '../filters/StaticFilters';
import StickyFilters from '../filters/StickyFilters';
import { scrollToTopOfPage } from '../shared/utils';
import { getMarketplaceIntegrationImpressionEnrichment } from '../bi/bigQuery/utils';
import { CTA_LOCATION } from '../bi/mixpanel/constants';
import useListingTracking from './hooks/useListingTracking';
import { useHandlers, useListingUpdate } from './hooks';
import { BRIEF_DATA, DEFAULT_BRAND_NAME } from './constants';
import classes from './index.module.scss';

const { reportScrollToTopClicked } = marketplaceIntegration;

const Listings = ({
    projects: initialProjects,
    briefData,
    briefId,
    setBriefData = noop,
    onNavigateToLogoServicesClick,
    rollouts,
}) => {
    const staticFiltersRef = useRef();
    const {
        isLoading,
        stickyFiltersVisible,
        projects,
        hasNextPage,
        loadMore,
        page: pageNumber,
    } = useListingUpdate({
        initialProjects,
        briefId,
        staticFiltersRef,
        briefData,
        setBriefData,
        rollouts,
    });
    const containerRef = useRef();
    const { handleBackCTAClick } = useHandlers({
        onNavigateToLogoServicesClick,
    });
    const trackingHandlers = useListingTracking({
        projects,
        briefId,
        containerRef,
    });
    const impressionEnrichment = getMarketplaceIntegrationImpressionEnrichment({
        pageNumber,
        briefId,
    });

    if (!projects) {
        return null;
    }

    return (
        <div ref={containerRef} className={classes.listingsWrapper}>
            <StaticFilters
                briefData={briefData}
                setBriefData={setBriefData}
                ref={staticFiltersRef}
            />
            <StickyFilters
                briefData={briefData}
                setBriefData={setBriefData}
                isVisible={stickyFiltersVisible}
                staticFiltersRef={staticFiltersRef}
            />
            <LogoProjectListings
                projects={projects}
                navigationParams={{
                    source: LISTING_SOURCE.MP_FORK,
                    brief_id: briefId,
                }}
                isLoading={isLoading}
                loadMore={hasNextPage ? loadMore : null}
                brandName={briefData.brandName || DEFAULT_BRAND_NAME}
                slogan={briefData.slogan}
                impressionEnrichment={impressionEnrichment}
                {...trackingHandlers}
            />

            {!hasNextPage && (
                <div className={classes.ctaWrapper}>
                    {onNavigateToLogoServicesClick && (
                        <Button
                            className={classes.backCta}
                            color="light-grey"
                            fill="ghost"
                            onClick={handleBackCTAClick}
                        >
                            <Icon
                                className={classes.backCtaIcon}
                                color="#404145"
                            >
                                {l_chevron_left}
                            </Icon>
                            <I18n k="variations_display.marketplace_listing.cta.back_to_logo_services" />
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            scrollToTopOfPage();
                            reportScrollToTopClicked(
                                CTA_LOCATION.BOTTOM_OF_LISTING
                            );
                        }}
                    >
                        <I18n k="variations_display.marketplace_listing.cta.edit_your_brief" />
                    </Button>
                </div>
            )}
        </div>
    );
};

Listings.propTypes = {
    projects: arrayOf(shape(LOGO_PROJECT_CARD)),
    briefData: shape(BRIEF_DATA),
    briefId: string,
    setBriefData: func,
    onNavigateToLogoServicesClick: func,
    rollouts: object,
};

export default Listings;
